import {Component, Output, EventEmitter, HostListener, OnDestroy} from '@angular/core';
import { CaseDocumentService } from 'src/app/services/documents.service';
import {ESCAPE} from "@angular/cdk/keycodes";

@Component({
	selector: 'app-docpreview',
	templateUrl: './docpreview.component.html',
	styleUrls: ['./docpreview.component.css']
})
export class DocpreviewComponent implements OnDestroy {

	public documentPreviewUrl: string | null = null;
	public docType: string = 'image';
	private previousBlobUrl: string | null = null;
	private retryCount: number = 0;
	private maxRetries: number = 2;
	private currentDocId: number = 0;
	@Output() cancelClicked = new EventEmitter();

	constructor(private caseDocumentService: CaseDocumentService) {
	}

	public ngOnInit() {

	}
	onShowHandler(docId: number, type: string = 'image') {
		if (docId === 0 || docId === undefined || docId === null) {
			return;
		}
		
		console.log('[DocPreview] Loading document:', { docId, type, retryCount: this.retryCount });
		
		// Clean up previous blob URL to prevent memory leaks
		if (this.previousBlobUrl) {
			URL.revokeObjectURL(this.previousBlobUrl);
			this.previousBlobUrl = null;
		}
		
		// Reset the URL to force re-render
		this.documentPreviewUrl = null;
		this.docType = type;
		this.currentDocId = docId;
		
		this.loadDocument(docId, type);
	}
	
	private loadDocument(docId: number, type: string) {
		try {
			this.caseDocumentService.getCaseDocumentFile(docId).subscribe(
				(data) => {
					console.log('[DocPreview] Document loaded successfully:', { 
						docId, 
						type, 
						size: data?.size || 'unknown',
						retryCount: this.retryCount 
					});
					
					if (!data || data.size === 0) {
						console.error('[DocPreview] Empty or invalid document data received');
						this.handleLoadError(new Error('Empty document data'), docId, type);
						return;
					}
					
					const blob = new Blob([data], { type: (type=='pdf'?'application/pdf':'image/jpeg') });
					const url = this.createDataUrl(data, type);
					this.previousBlobUrl = url;
					
					// Small delay to ensure DOM updates, especially for PDFs
					setTimeout(() => {
						this.documentPreviewUrl = url;
						this.retryCount = 0; // Reset retry counter on success
						console.log('[DocPreview] Document rendered:', { docId, url: url.substring(0, 50) + '...' });
					}, 0);
				},
				(error) => {
					console.error('[DocPreview] Error loading document:', {
						docId,
						type,
						retryCount: this.retryCount,
						error: error?.message || error,
						status: error?.status,
						statusText: error?.statusText
					});
					this.handleLoadError(error, docId, type);
				}
			);
		} catch (err) { 
			console.error('[DocPreview] Exception while loading document:', { docId, type, error: err });
			this.handleLoadError(err, docId, type);
		}
	}
	
	private handleLoadError(error: any, docId: number, type: string) {
		if (this.retryCount < this.maxRetries) {
			this.retryCount++;
			console.warn('[DocPreview] Retrying document load:', { 
				docId, 
				type, 
				attempt: this.retryCount, 
				maxRetries: this.maxRetries 
			});
			
			// Exponential backoff: 500ms, 1000ms, 2000ms...
			const delay = 500 * Math.pow(2, this.retryCount - 1);
			setTimeout(() => {
				this.loadDocument(docId, type);
			}, delay);
		} else {
			console.error('[DocPreview] Max retries reached. Failed to load document:', { 
				docId, 
				type, 
				totalAttempts: this.retryCount + 1 
			});
			this.documentPreviewUrl = './assets/img_na.png';
			this.retryCount = 0; // Reset for next attempt
		}
	}
	
	private createDataUrl(data: any, type:string): string {
		if(type=='pdf') {
			return URL.createObjectURL(data);
		} else {
			const blob = new Blob([data], { type:'image/jpeg'});
			return URL.createObjectURL(blob);
		}
	}
	closeHandler() {
		console.log('[DocPreview] Closing document preview:', { docId: this.currentDocId });
		this.retryCount = 0; // Reset retry counter on close
		// Clean up blob URL when closing
		if (this.previousBlobUrl) {
			URL.revokeObjectURL(this.previousBlobUrl);
			this.previousBlobUrl = null;
		}
		this.documentPreviewUrl = null;
		this.cancelClicked.emit();
	}
	
	ngOnDestroy() {
		console.log('[DocPreview] Component destroyed, cleaning up resources');
		// Clean up blob URL when component is destroyed
		if (this.previousBlobUrl) {
			URL.revokeObjectURL(this.previousBlobUrl);
		}
		this.retryCount = 0;
	}
}
